import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faPencil,
  faPlus,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import BridgeSelectionModal, {
  buildPathSummary,
  flattenHierarchyRows,
  getAreaEntityLabel,
  getAreaDataLevel,
  getEnabledLevels,
  normalizeIds,
} from "./BridgeSelectionModal";

const createSelectionId = () =>
  `bridge-selection-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createElementsFromRows = (rows) =>
  (rows || []).map((row) => ({
    id: row.id,
    total_area: row.area,
  }));

const BridgeSelectionManager = ({
  bridgeId,
  selectedBridge: selectedBridgeProp = null,
  initialSelectedIds = [],
  onSelectionChange,
  title = "Select Elements",
  required = false,
  emptyMessage = "Add one or more bridge selections to choose the elements.",
  containerClassName = "bg-white shadow rounded-lg p-6",
  showSelectionSummary = true,
}) => {
  const [fetchedBridge, setFetchedBridge] = useState(null);
  const [bridgeSelections, setBridgeSelections] = useState([]);
  const [showBridgeSelectionModal, setShowBridgeSelectionModal] = useState(false);
  const [editingBridgeSelectionId, setEditingBridgeSelectionId] = useState(null);
  const [selectionTableRows, setSelectionTableRows] = useState([]);
  const [loadingSelectionTable, setLoadingSelectionTable] = useState(false);
  const [initialHydrationComplete, setInitialHydrationComplete] = useState(false);
  const [isSelectionSummaryCollapsed, setIsSelectionSummaryCollapsed] =
    useState(true);
  const hydratedInitialSelectionRef = useRef(null);
  const lastNotifiedSelectionRef = useRef(null);

  const selectedBridge = selectedBridgeProp || fetchedBridge;

  const normalizedInitialSelectedIds = useMemo(
    () => normalizeIds(initialSelectedIds),
    [initialSelectedIds],
  );
  const initialSelectionSignature = normalizedInitialSelectedIds.join(",");

  useEffect(() => {
    const fetchBridgeDetails = async () => {
      if (!bridgeId || selectedBridgeProp) {
        setFetchedBridge(null);
        return;
      }

      try {
        const response = await api.get(`/bridges/${bridgeId}`);
        setFetchedBridge(response.data?.success ? response.data.data : null);
      } catch (error) {
        console.error("Error fetching bridge details for selection manager:", error);
        setFetchedBridge(null);
      }
    };

    fetchBridgeDetails();
  }, [bridgeId, selectedBridgeProp]);

  useEffect(() => {
    setBridgeSelections([]);
    setShowBridgeSelectionModal(false);
    setEditingBridgeSelectionId(null);
    setSelectionTableRows([]);
    setIsSelectionSummaryCollapsed(true);
    hydratedInitialSelectionRef.current = null;
    lastNotifiedSelectionRef.current = null;
    setInitialHydrationComplete(false);
  }, [bridgeId]);

  const activeBridgeSelections = useMemo(
    () => bridgeSelections.filter((selection) => selection.active),
    [bridgeSelections],
  );

  const activeSelectedElementIds = useMemo(
    () =>
      Array.from(
        new Set(activeBridgeSelections.flatMap((selection) => selection.elementIds || [])),
      ),
    [activeBridgeSelections],
  );

  const activeSelectionArea = useMemo(
    () =>
      activeBridgeSelections.reduce(
        (sum, selection) => sum + (Number(selection.area) || 0),
        0,
      ),
    [activeBridgeSelections],
  );

  const totalAreaDataEntityLabel = useMemo(
    () => getAreaEntityLabel(selectedBridge),
    [selectedBridge],
  );

  const selectionSummaryLevels = useMemo(
    () =>
      getEnabledLevels(selectedBridge)
        .filter((levelNum) => levelNum <= getAreaDataLevel(selectedBridge))
        .sort((left, right) => left - right),
    [selectedBridge],
  );

  const selectionSummaryText = `${activeSelectedElementIds.length} ${totalAreaDataEntityLabel.toLowerCase()} selected`;
  const selectionAreaText = `${(activeSelectionArea || 0).toFixed(2)} m²`;

  const hydrateSelection = useCallback(
    async (elementIds, existingId = null) => {
      const normalizedIds = normalizeIds(elementIds);
      if (!bridgeId || normalizedIds.length === 0) return null;

      const response = await api.post("/new-simulation/hierarchy-structure", {
        bridgeId: Number(bridgeId),
        elementIds: normalizedIds,
      });

      const hierarchy = response.data?.success ? response.data?.data?.hierarchy : [];
      const rows = flattenHierarchyRows(hierarchy);
      const area = rows.reduce((sum, row) => sum + (Number(row.area) || 0), 0);

      return {
        id: existingId || createSelectionId(),
        active: true,
        elementIds: normalizedIds,
        elements: createElementsFromRows(rows),
        rows,
        area,
        pathSummary: buildPathSummary(rows, selectedBridge, normalizedIds.length),
        areaEntityLabel: totalAreaDataEntityLabel,
      };
    },
    [bridgeId, selectedBridge, totalAreaDataEntityLabel],
  );

  useEffect(() => {
    const hydrateInitialSelection = async () => {
      if (
        !bridgeId ||
        !selectedBridge ||
        normalizedInitialSelectedIds.length === 0 ||
        bridgeSelections.length > 0 ||
        hydratedInitialSelectionRef.current === initialSelectionSignature
      ) {
        return;
      }

      hydratedInitialSelectionRef.current = initialSelectionSignature;

      try {
        const hydratedSelection = await hydrateSelection(normalizedInitialSelectedIds);
        if (hydratedSelection) {
          setBridgeSelections([hydratedSelection]);
        }
      } catch (error) {
        console.error("Error hydrating initial bridge selection:", error);
      } finally {
        setInitialHydrationComplete(true);
      }
    };

    if (normalizedInitialSelectedIds.length === 0) {
      setInitialHydrationComplete(true);
    }
    hydrateInitialSelection();
  }, [
    bridgeId,
    bridgeSelections.length,
    hydrateSelection,
    initialSelectionSignature,
    normalizedInitialSelectedIds,
    selectedBridge,
  ]);

  useEffect(() => {
    if (!initialHydrationComplete && normalizedInitialSelectedIds.length > 0) {
      return;
    }

    const selectionSignature = activeSelectedElementIds.join(",");
    if (lastNotifiedSelectionRef.current === selectionSignature) return;

    lastNotifiedSelectionRef.current = selectionSignature;

    if (typeof onSelectionChange === "function") {
      const selectedElements = activeBridgeSelections.flatMap(
        (selection) => selection.elements || [],
      );
      onSelectionChange(activeSelectedElementIds, selectedElements, activeBridgeSelections);
    }
  }, [
    activeBridgeSelections,
    activeSelectedElementIds,
    initialHydrationComplete,
    normalizedInitialSelectedIds.length,
    onSelectionChange,
  ]);

  useEffect(() => {
    const buildSelectionTableRows = async () => {
      if (!bridgeId || activeSelectedElementIds.length === 0) {
        setSelectionTableRows([]);
        return;
      }

      try {
        setLoadingSelectionTable(true);
        const response = await api.post("/new-simulation/hierarchy-structure", {
          bridgeId: Number(bridgeId),
          elementIds: activeSelectedElementIds,
        });

        const hierarchy = response.data?.success ? response.data?.data?.hierarchy : [];
        setSelectionTableRows(flattenHierarchyRows(hierarchy));
      } catch (error) {
        console.error("Error building bridge selection summary rows:", error);
        setSelectionTableRows([]);
      } finally {
        setLoadingSelectionTable(false);
      }
    };

    buildSelectionTableRows();
  }, [activeSelectedElementIds, bridgeId]);

  const handleOpenAddBridgeSelection = () => {
    setEditingBridgeSelectionId(null);
    setShowBridgeSelectionModal(true);
  };

  const handleOpenEditBridgeSelection = (selectionId) => {
    setEditingBridgeSelectionId(selectionId);
    setShowBridgeSelectionModal(true);
  };

  const handleCloseBridgeSelectionModal = () => {
    setShowBridgeSelectionModal(false);
    setEditingBridgeSelectionId(null);
  };

  const handleSaveBridgeSelection = (selectionDetails) => {
    const savedAt = Date.now();
    setBridgeSelections((prevSelections) => {
      if (editingBridgeSelectionId) {
        return prevSelections.map((selection) =>
          selection.id === editingBridgeSelectionId
            ? {
                ...selection,
                ...selectionDetails,
                updatedAt: savedAt,
              }
            : selection,
        );
      }

      return [
        ...prevSelections,
        {
          id: createSelectionId(),
          active: true,
          ...selectionDetails,
          createdAt: savedAt,
          updatedAt: savedAt,
        },
      ];
    });
    handleCloseBridgeSelectionModal();
  };

  const handleToggleBridgeSelection = (selectionId) => {
    setBridgeSelections((prevSelections) =>
      prevSelections.map((selection) =>
        selection.id === selectionId
          ? { ...selection, active: !selection.active }
          : selection,
      ),
    );
  };

  const handleDeleteBridgeSelection = (selectionId) => {
    setBridgeSelections((prevSelections) =>
      prevSelections.filter((selection) => selection.id !== selectionId),
    );
    if (editingBridgeSelectionId === selectionId) {
      handleCloseBridgeSelectionModal();
    }
  };

  const editingBridgeSelection = bridgeSelections.find(
    (selection) => selection.id === editingBridgeSelectionId,
  );

  if (!bridgeId) {
    return null;
  }

  return (
    <div className={containerClassName}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            {title} {required && <span className="text-red-500">*</span>}
          </h2>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-base font-bold text-gray-900 sm:text-lg">
              {selectionSummaryText}
            </span>
            <span className="text-base font-bold text-blue-700 sm:text-lg">
              • {selectionAreaText}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleOpenAddBridgeSelection}
          disabled={!selectedBridge}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Bridge Selection
        </button>
      </div>

      <div className="overflow-hidden rounded-md border border-gray-200">
        {bridgeSelections.length === 0 ? (
          <div className="bg-gray-50 px-4 py-6 text-sm text-gray-600">
            {emptyMessage}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">
                    Selection Path
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">
                    Area (m²)
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">
                    Active
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {bridgeSelections.map((selection) => (
                  <tr
                    key={selection.id}
                    className={selection.active ? "" : "bg-gray-50 text-gray-500"}
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-gray-900">
                        {selection.pathSummary ||
                          `${selection.elementIds?.length || 0} ${totalAreaDataEntityLabel.toLowerCase()}`}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {selection.elementIds?.length || 0} {totalAreaDataEntityLabel.toLowerCase()}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right align-top font-medium text-gray-900">
                      {(Number(selection.area) || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center align-top">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={selection.active}
                        onClick={() => handleToggleBridgeSelection(selection.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${selection.active ? "bg-blue-600" : "bg-gray-300"}`}
                        title={selection.active ? "Disable selection" : "Enable selection"}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${selection.active ? "translate-x-6" : "translate-x-1"}`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenEditBridgeSelection(selection.id)}
                          className="rounded p-2 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                          title="Edit selection"
                        >
                          <FontAwesomeIcon icon={faPencil} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteBridgeSelection(selection.id)}
                          className="rounded p-2 text-red-600 hover:bg-red-50 hover:text-red-800"
                          title="Delete selection"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showSelectionSummary && (
        <div className="mt-4 overflow-hidden rounded-md border border-gray-300 bg-gray-50">
          <button
            type="button"
            onClick={() => setIsSelectionSummaryCollapsed((prev) => !prev)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
          >
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-700">
                Selection Summary
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Expand to review the selected {totalAreaDataEntityLabel.toLowerCase()}.
              </p>
            </div>
            <span className="flex items-center gap-3">
              <span className="hidden text-sm font-semibold text-gray-700 sm:inline">
                {selectionSummaryText}
              </span>
              <FontAwesomeIcon
                icon={
                  isSelectionSummaryCollapsed ? faChevronRight : faChevronDown
                }
                className="text-gray-500"
              />
            </span>
          </button>

          {!isSelectionSummaryCollapsed &&
            (loadingSelectionTable ? (
              <div className="border-t border-gray-300 px-4 py-3 text-sm text-gray-500">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Building table...
              </div>
            ) : (
              <div className="border-t border-gray-300 p-4">
                <div
                  className="overflow-x-auto overflow-y-auto"
                  style={{ maxHeight: "390px" }}
                >
                  <table className="min-w-full text-sm border border-gray-200 rounded bg-white">
                    <thead className="bg-gray-100">
                      <tr>
                        {selectionSummaryLevels.map((levelNum) => (
                          <th
                            key={levelNum}
                            className="px-3 py-2 text-left font-semibold text-gray-700 sticky top-0 bg-gray-100 z-10"
                          >
                            {(() => {
                              const levelLabels =
                                typeof selectedBridge?.levelLabels === "string"
                                  ? JSON.parse(selectedBridge.levelLabels)
                                  : selectedBridge?.levelLabels || {};
                              return levelLabels?.[`level${levelNum}`]?.label || `Level ${levelNum}`;
                            })()}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-right font-semibold text-gray-700 sticky top-0 bg-gray-100 z-10">
                          Area (m²)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectionTableRows.map((row) => (
                        <tr key={row.id} className="border-t border-gray-200">
                          {selectionSummaryLevels.map((levelNum) => (
                            <td
                              key={`${row.id}-${levelNum}`}
                              className="px-3 py-2 text-gray-900"
                            >
                              {row.levels[`level${levelNum}`] ||
                                (levelNum === 1
                                  ? row.direction || "-"
                                  : levelNum === 2
                                    ? row.position || "-"
                                    : "-")}
                            </td>
                          ))}
                          <td className="px-3 py-2 text-right text-gray-900">
                            {(row.area || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </div>
      )}

      <BridgeSelectionModal
        isOpen={showBridgeSelectionModal}
        bridgeId={bridgeId}
        selectedBridge={selectedBridge}
        initialSelectedIds={editingBridgeSelection?.elementIds || []}
        onClose={handleCloseBridgeSelectionModal}
        onSave={handleSaveBridgeSelection}
        title={editingBridgeSelection ? "Edit Bridge Selection" : "Add Bridge Selection"}
        saveLabel={editingBridgeSelection ? "Save Changes" : "Save Selection"}
      />
    </div>
  );
};

export default BridgeSelectionManager;
